/** Filename heuristics for the bulk invoice upload.
 *
 * Kept out of the dialog component because exporting non-components from a
 * component module breaks Vite Fast Refresh — and because these are pure and
 * worth testing on their own.
 */
import type { IntegrationVendor } from "./api";

/** Best-guess vendor for a dropped file, from its name.
 *
 *  Deliberately a *guess* shown in an editable dropdown, never an automatic
 *  assignment: filenames like "Sage August INV00077648" and "Autodata August
 *  INV00077639" differ by one word, and silently filing an invoice against the
 *  wrong vendor would put its lines on the wrong feed's roster. */
export function guessVendor(filename: string, vendors: IntegrationVendor[]): string {
  const hay = filename.toLowerCase().replace(/[^a-z0-9]+/g, " ");

  const scored = vendors.map((v) => {
    // Score on the vendor's own words plus its slug, longest word wins, so
    // "protractor" beats a bare "vehlo" when both appear.
    const tokens = [
      ...v.name.toLowerCase().split(/[^a-z0-9]+/),
      ...v.slug.split("_"),
    ].filter((t) => t.length >= 3);
    let score = 0;
    for (const t of tokens) if (hay.includes(t)) score = Math.max(score, t.length);
    return { id: v.id, score };
  });

  const best = Math.max(0, ...scored.map((s) => s.score));
  if (best === 0) return "";

  // A TIE means the filename genuinely doesn't say which vendor it is, and
  // guessing would be worse than not guessing. "Vehlo Invoice-INV-945440.pdf"
  // is the real case: that one invoice bills Protractor AND Tire Guru, which
  // are separate feeds with separate rooftop counts, so picking either
  // arbitrarily files half the money against the wrong roster.
  const winners = scored.filter((s) => s.score === best);
  return winners.length === 1 ? winners[0]!.id : "";
}

/** "ISI July Invoice_INV4330151_1785508115517.pdf" -> "INV4330151"
 *
 *  Tokenised rather than regexed over the whole name: `_` is a word character,
 *  so `\b` never fires inside "x_INV4330151_1785508115517" and a naive boundary
 *  match finds nothing at all.
 *
 *  A letter-prefixed number always wins over a bare one, which is what keeps
 *  the 13-digit epoch suffix these files carry from beating the real invoice
 *  number next to it. */
export function guessInvoiceNumber(filename: string): string {
  const stem = filename.replace(/\.[a-z0-9]+$/i, "");
  const tokens = stem.split(/[^A-Za-z0-9-]+/).filter(Boolean);

  // Pass 1 — "INV4330151", "M0192818", "INV-945440".
  for (const token of tokens) {
    const m = /[A-Za-z]{1,4}-?\d{5,}$/.exec(token);
    if (m) return m[0];
  }
  // Pass 2 — a bare number, e.g. "Invoice_95795114_from_AutoNetTV".
  for (const token of tokens) {
    if (/^\d{5,}$/.test(token)) return token;
  }
  return "";
}
